import { Color, ConeGeometry, CylinderGeometry, Group, Mesh, MeshStandardNodeMaterial } from 'three/webgpu'
import type { Obstacle } from './Obstacle.ts'

export interface IslandFeature extends Obstacle {
  radius: number
}

const ISLAND_COUNT = 9

/** Seeded scenery for Open Water: broad, low grassy islands. */
export class OpenWaterIslands {
  readonly group = new Group()
  readonly features: IslandFeature[] = []
  private readonly meshes: Mesh[] = []

  constructor(seed = 0x51a7e) {
    this.group.name = 'open-water-islands'
    let state = seed >>> 0
    const random = (): number => {
      state = (Math.imul(state, 1664525) + 1013904223) >>> 0
      return state / 4294967296
    }

    const sand = new MeshStandardNodeMaterial({ color: new Color('#c8b07a'), roughness: 1 })
    const green = new MeshStandardNodeMaterial({ color: new Color('#527555'), roughness: 1 })

    for (let i = 0; i < ISLAND_COUNT; i++) {
      // Keep the departure corridor open. Islands begin well beyond the first
      // view, but are still close enough to become useful navigation marks.
      const angle = random() * Math.PI * 2
      const distance = 520 + random() * 980
      const radius = 38 + random() * 62
      const x = Math.sin(angle) * distance
      const z = Math.cos(angle) * distance
      const feature: IslandFeature = { x, z, halfWidth: radius, halfLength: radius * (.72 + random() * .35), rotation: random() * Math.PI, radius }
      this.features.push(feature)

      const base = new Mesh(new CylinderGeometry(radius * 1.1, radius * 1.3, 5, 12), sand)
      base.position.set(x, -1.5, z)
      base.rotation.y = feature.rotation
      base.castShadow = true
      base.receiveShadow = true
      this.group.add(base)
      this.meshes.push(base)

      const shore = new Mesh(new ConeGeometry(radius * .96, 5.5, 12), green)
      shore.position.set(x, 1.25, z)
      shore.rotation.y = feature.rotation
      shore.castShadow = true
      shore.receiveShadow = true
      this.group.add(shore)
      this.meshes.push(shore)

      // Preserve island positions from the original seed sequence, which
      // consumed five extra samples for a peak on every third island.
      if (i % 3 === 0) for (let sample = 0; sample < 5; sample++) random()
    }
  }

  get obstacles(): readonly Obstacle[] {
    return this.features
  }

  dispose(): void {
    for (const mesh of this.meshes) {
      mesh.geometry.dispose()
      if (Array.isArray(mesh.material)) mesh.material.forEach(material => material.dispose())
      else mesh.material.dispose()
    }
    this.meshes.length = 0
    this.features.length = 0
    this.group.removeFromParent()
    this.group.clear()
  }
}
